import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Avatar from "../../ui/avatar/Avatar";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner";
import LeadPersonPicker, {
  getPersonDisplayName,
  usePersonPickerActions,
} from "./LeadPersonPicker";

import styles from "./LeadPersonsSection.module.css";

const MAX_STACKED_AVATARS = 3;
const PICKER_MAX_HEIGHT = 320;
const PICKER_MIN_HEIGHT = 180;
const PICKER_VIEWPORT_MARGIN = 8;
const PICKER_GAP = 4;

const findScrollParent = (element) => {
  let current = element?.parentElement;

  while (current && current !== document.body) {
    const { overflowY } = window.getComputedStyle(current);
    if (overflowY === "auto" || overflowY === "scroll") return current;
    current = current.parentElement;
  }

  return null;
};

const isOutsideVerticalRange = (rect, containerRect) =>
  Boolean(containerRect) &&
  (rect.bottom < containerRect.top || rect.top > containerRect.bottom);

const isRecipient = (person) => person?.role === "receptor";

const getStarTitle = ({ lockedRecipient, missingEmail }) => {
  if (lockedRecipient) return "El lead necesita al menos un receptor";
  if (missingEmail) return "La persona necesita un email para ser receptora";
  return undefined;
};

const LeadPersonsSection = ({
  persons = [],
  personPicker = {},
  pendingPersonIds = [],
  onAddPerson,
  onRemovePerson,
  onToggleRecipient,
}) => {
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPosition, setPickerPosition] = useState(null);
  const listRef = useRef(null);
  const addButtonRef = useRef(null);
  const pickerRef = useRef(null);

  const { saving, error, clearError, selectPerson } = usePersonPickerActions({
    onAddPerson,
    onAdded: (addedPerson) => {
      if (addedPerson) setPickerOpen(false);
    },
  });

  const closePicker = useCallback(() => {
    setPickerOpen(false);
    clearError();
  }, [clearError]);

  const updatePickerPosition = useCallback(() => {
    const anchorRect = listRef.current?.getBoundingClientRect();
    if (!anchorRect) return;

    const buttonRect =
      addButtonRef.current?.getBoundingClientRect() ?? anchorRect;
    const scrollParentRect = findScrollParent(
      addButtonRef.current,
    )?.getBoundingClientRect();

    if (isOutsideVerticalRange(buttonRect, scrollParentRect)) {
      closePicker();
      return;
    }

    const spaceBelow =
      window.innerHeight - buttonRect.bottom - PICKER_GAP - PICKER_VIEWPORT_MARGIN;
    const spaceAbove = buttonRect.top - PICKER_GAP - PICKER_VIEWPORT_MARGIN;
    const opensBelow =
      spaceBelow >= PICKER_MIN_HEIGHT || spaceBelow >= spaceAbove;
    const height = Math.min(
      PICKER_MAX_HEIGHT,
      Math.max(opensBelow ? spaceBelow : spaceAbove, PICKER_MIN_HEIGHT),
    );
    const farthestOffset = window.innerHeight - PICKER_VIEWPORT_MARGIN - height;

    setPickerPosition({
      left: anchorRect.left,
      width: anchorRect.width,
      height,
      ...(opensBelow
        ? { top: Math.min(buttonRect.bottom + PICKER_GAP, farthestOffset) }
        : {
            bottom: Math.min(
              window.innerHeight - buttonRect.top + PICKER_GAP,
              farthestOffset,
            ),
          }),
    });
  }, [closePicker]);

  useEffect(() => {
    if (!pickerOpen) {
      setPickerPosition(null);
      return undefined;
    }

    updatePickerPosition();

    const handleMouseDown = (event) => {
      if (pickerRef.current?.contains(event.target)) return;
      if (addButtonRef.current?.contains(event.target)) return;
      closePicker();
    };
    const handleScroll = (event) => {
      if (pickerRef.current?.contains(event.target)) return;
      updatePickerPosition();
    };

    document.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", closePicker);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", closePicker);
    };
  }, [pickerOpen, updatePickerPosition, closePicker]);

  const pickerPopover =
    pickerOpen && pickerPosition
      ? createPortal(
          <div
            ref={pickerRef}
            className={styles.pickerPopover}
            style={pickerPosition}
          >
            <LeadPersonPicker
              {...personPicker}
              fillContainer
              onSelect={selectPerson}
              onClose={closePicker}
              saving={saving}
              error={error}
            />
          </div>,
          document.body,
        )
      : null;

  const pendingIds = new Set(pendingPersonIds.map((id) => String(id)));
  const recipients = persons.filter(isRecipient);
  const orderedPersons = [
    ...recipients,
    ...persons.filter((person) => !isRecipient(person)),
  ];
  const countLabel =
    persons.length === 1 ? "1 persona" : `${persons.length} personas`;

  return (
    <div className={styles.section}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => {
          if (open) closePicker();
          setOpen((previous) => !previous);
        }}
        aria-expanded={open}
      >
        <span className={styles.avatars}>
          {orderedPersons.slice(0, MAX_STACKED_AVATARS).map((person) => (
            <span key={person.id} className={styles.avatarWrap}>
              <Avatar
                src={person.photo}
                name={getPersonDisplayName(person)}
                size={20}
              />
            </span>
          ))}
        </span>
        <span className={styles.count}>{countLabel}</span>
        <span
          className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {open && (
        <div className={styles.list} ref={listRef}>
          {persons.length === 0 && (
            <div className={styles.empty}>El lead no tiene personas cargadas.</div>
          )}

          {orderedPersons.map((person) => {
            const displayName = getPersonDisplayName(person);
            const meta = [person.position, person.email || "Sin email"]
              .filter(Boolean)
              .join(" · ");
            const recipient = isRecipient(person);
            const lockedRecipient = recipient && recipients.length <= 1;
            const missingEmail = !recipient && !person.email;
            const pending = pendingIds.has(String(person.id));

            return (
              <div key={person.id} className={styles.person}>
                <button
                  type="button"
                  className={`${styles.star} ${recipient ? styles.starActive : ""}`}
                  onClick={() => onToggleRecipient?.(person)}
                  disabled={
                    saving ||
                    pendingIds.size > 0 ||
                    lockedRecipient ||
                    missingEmail ||
                    !onToggleRecipient
                  }
                  aria-pressed={recipient}
                  aria-busy={pending}
                  aria-label={
                    recipient
                      ? `Quitar a ${displayName} como receptor`
                      : `Marcar a ${displayName} como receptor`
                  }
                  title={getStarTitle({ lockedRecipient, missingEmail })}
                >
                  {pending ? (
                    <span data-testid="person-pending-spinner">
                      <LoadingSpinner size={14} thickness={2} />
                    </span>
                  ) : (
                    <span aria-hidden="true">{recipient ? "★" : "☆"}</span>
                  )}
                </button>
                <Avatar src={person.photo} name={displayName} size={24} />
                <div className={styles.personInfo}>
                  <div className={styles.personName}>{displayName}</div>
                  <div className={styles.personMeta}>{meta}</div>
                </div>
                {!recipient && onRemovePerson && (
                  <button
                    type="button"
                    className={styles.remove}
                    onClick={() => onRemovePerson(person)}
                    aria-label={`Sacar ${displayName} del lead`}
                    aria-busy={pending}
                    disabled={saving || pendingIds.size > 0}
                  >
                    {pending ? (
                      <span data-testid="person-pending-spinner">
                        <LoadingSpinner size={14} thickness={2} />
                      </span>
                    ) : (
                      "×"
                    )}
                  </button>
                )}
              </div>
            );
          })}

          {onAddPerson && (
            <button
              ref={addButtonRef}
              type="button"
              className={`${styles.add} ${pickerOpen ? styles.addActive : ""}`}
              onClick={() => (pickerOpen ? closePicker() : setPickerOpen(true))}
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              ＋ Agregar persona
            </button>
          )}
        </div>
      )}

      {pickerPopover}
    </div>
  );
};

export default LeadPersonsSection;
